import { React, Fragment, useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import {
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Input,
  FormFeedback,
} from "reactstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import AddressBookList from "./AddressBookList";
import AddressBookLoginForm from "./AddressBookLoginForm";
import SelectOption from "../Forms/Fields/SelectOption";

import { addressBookActions } from "../../store/address-book-slice";
import {
  fetchAddressBookData,
  sendAddressBookUpdate,
  sendAddressDelete,
} from "../../store/address-book-actions";

function AddressBook(props) {
  const dispatch = useDispatch();

  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );

  const addressBookState = useSelector((state) => state.addressBook);
  const alertMessage = addressBookState.StatusMessage.message;
  const isAlertError = addressBookState.StatusMessage.error;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const [showEditAddress, setShowEditAddress] = useState(false);
  const [currentEditAddressId, setCurrentEditAddressId] = useState(null);
  const [editFormValues, setEditFormValues] = useState({
    Title: "",
    FirstName: "",
    LastName: "",
    Address1: "",
    Address2: "",
    Country: "",
    State: "",
    Province: "",
    City: "",
    ZipPostalCode: "",
  });

  // load address book data on component init
  const isInitialRef = useRef(false);
  useEffect(() => {
    if (isInitialRef.current) return;
    isInitialRef.current = true;

    const fetchData = async () => {
      setIsLoading(true);
      loadAddressBook();
    };

    fetchData();
  }, []);

  // reload address book data when someone logs in
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      loadAddressBook();
    };

    if (addressBookState.IsAfterLogin) {
      fetchData();
      dispatch(addressBookActions.resetAfterLogin(addressBookState.IsAdding));
    }

  }, [dispatch, addressBookState.IsAfterLogin]);

  // add address to book when user is logged in and add address action is requested
  const addressAddedRef = useRef(false);
  useEffect(() => {
    if(addressBookState.IsDonorLoggedIn && (addressBookState.IsAdding || addressBookState.IsAddingAfterLogin)) {
      if (addressAddedRef.current) return;
      addressAddedRef.current = true;

      dispatch(sendAddressBookUpdate(addressBookState.AddressToAdd));
      dispatch(addressBookActions.resetAddingAddress());
    }
  }, [addressBookState.IsAdding, addressBookState.IsAddingAfterLogin]);

  // apply status message when the alert message is set or changes
  useEffect(() => {
    if(alertMessage) {
      setShowAlertMessage(true);
    }
    return () => {
      setTimeout(function () {
        setShowAlertMessage(false);
        dispatch(addressBookActions.setStatusMessage({message: '', error: false}));
      }, 5000);
    };
  }, [alertMessage]);

  // get title list
  const titles = currentRequest.Lists.DonorTitles;
  const titlesArray = titles.map((item) => {
    return item.Value;
  });

  // get country list
  const countries = currentRequest.Lists.Countries;
  const countriesArray = countries.map((item) => {
    return item.Value;
  });

  // get state list
  const states = currentRequest.Lists.States;
  const statesArray = states.map((item) => {
    return item.Value;
  });

  // get province list
  const provinces = currentRequest.Lists.Provinces;
  const provincesArray = provinces.map((item) => {
    return item.Value;
  });

  // init form validation schema
  const formSchema = Yup.object().shape({
    FirstName: Yup.string().required("Required"),
    LastName: Yup.string().required("Required"),
    EmailAddress: Yup.string()
      .email("Please enter a valid email address"),
    Address1: Yup.string().required("Required"),
    Country: Yup.string().required("Required"),
    State: Yup.string().when("Country", (country) => {
      if (country === "USA") return Yup.string().required("Required");
    }),
    Province: Yup.string().when("Country", (country) => {
      if (country === "Canada") return Yup.string().required("Required");
    }),
    City: Yup.string().required("Required"),
    ZipPostalCode: Yup.string().when("Country", (country) => {
      if (country === "USA" || country === "Canada") {
        if(country === "USA") {
          return Yup.string().required("Required").matches(/^[0-9]{5}(?:-[0-9]{4})?$/, "Must be a valid zip code");
        } else {
          return Yup.string().required("Required").matches(/^(?!.*[DFIOQU])[A-VXY][0-9][A-Z][ -]?[0-9][A-Z][0-9]$/, "Must be a valid postal code");
        }
      }
    }),
  });

  function loadAddressBook() {
    dispatch(fetchAddressBookData());
    setIsLoaded(true);
    setIsLoading(false);
  }

  function addressEditClickHandler(address) {
    let editAddress = { ...address };

    setCurrentEditAddressId(editAddress.Id);

    if (editAddress.Country === "USA") {
      editAddress.State = editAddress.StateProvince;
    }
    if (editAddress.Country === "Canada") {
      editAddress.Province = editAddress.StateProvince;
    }
    setEditFormValues(editAddress);

    setShowEditAddress(true);
  }

  function addressDeleteClickHandler(address) {
    dispatch(sendAddressDelete(address));
  }

  function formSubmitHandler(address) {
    if (address.Country === "USA") {
      address.StateProvince = address.State;
    } else if (address.Country === "Canada") {
      address.StateProvince = address.Province;
    } else {
      address.StateProvince = "";
    }
    address.Id = currentEditAddressId;
    address.UserId = currentRequest.DonorInfo.Id;

    dispatch(sendAddressBookUpdate(address));
    setShowEditAddress(false);
  }

  return (
    <Fragment>
      <Modal
        className="address-book-modal"
        isOpen={props.showAddressBook}
        toggle={() =>
          props.onToggleAddressBookClickHandler(!props.showAddressBook)
        }
        size="lg"
      >
        <ModalHeader
          toggle={() =>
            props.onToggleAddressBookClickHandler(!props.showAddressBook)
          }
        >
          Address Book
        </ModalHeader>
        <ModalBody>
          <Container>
            <Row>
              <Col lg="12" className="text-center">
                {showAlertMessage && (
                  <div
                    className={`alert alert-${
                      isAlertError ? "danger" : "primary"
                    } animate grow`}
                    role="alert"
                  >
                    {alertMessage}
                  </div>
                )}

                {isLoaded && (
                  <div>
                    {addressBookState.IsDonorLoggedIn && (
                      <AddressBookList
                        addresses={addressBookState.DonorAddresses}
                        onSelectClickHandler={props.onSelectClickHandler}
                        onAddressEditClickHandler={addressEditClickHandler}
                        onAddressDeleteClickHandler={addressDeleteClickHandler}
                      />
                    )}
                    {!addressBookState.IsDonorLoggedIn && (
                      <AddressBookLoginForm />
                    )}
                  </div>
                )}
                {(isLoading) && (
                  <div className="loading-spinner">
                    <Spinner color="primary">Loading...</Spinner>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </ModalBody>
      </Modal>
      <Modal
        className="address-book-modal"
        isOpen={showEditAddress}
        toggle={() => setShowEditAddress(!showEditAddress)}
        size="lg"
      >
        <ModalHeader toggle={() => setShowEditAddress(!showEditAddress)}>
          Edit Address
        </ModalHeader>
        <ModalBody>
          <Container>
            <Row>
              <Col lg="12" className="text-center">
                <Formik
                  initialValues={editFormValues}
                  validationSchema={formSchema}
                  onSubmit={formSubmitHandler}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    setFieldTouched,
                  }) => (
                    <Form id="editAddressForm" className="text-start">
                      <Container>
                        <Row className="mb-4">
                          <Col xs="12">
                            <label htmlFor="Title">Title:</label>
                            <Input
                              type="select"
                              name="Title"
                              value={values.Title}
                              className="form-control"
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                            >
                              <SelectOption key="" label="Select..." value="" />
                              {titlesArray.map((option) => (
                                <SelectOption
                                  key={option}
                                  label={option}
                                  value={option}
                                  selected={option === values.Title}
                                />
                              ))}
                            </Input>
                          </Col>
                        </Row>
                        <Row className="mb-4">
                          <Col xs="12">
                            <label className="required" htmlFor="FirstName">
                              First Name:
                            </label>
                            <Input
                              name="FirstName"
                              id="FirstName"
                              className="form-control"
                              value={values.FirstName}
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              invalid={Boolean(
                                touched["FirstName"] && errors["FirstName"]
                              )}
                            />
                            {touched["FirstName"] && errors["FirstName"] ? (
                              <FormFeedback>{errors["FirstName"]}</FormFeedback>
                            ) : (
                              ""
                            )}
                          </Col>
                        </Row>
                        <Row className="mb-4">
                          <Col xs="12">
                            <label className="required" htmlFor="LastName">
                              Last Name:
                            </label>
                            <Input
                              name="LastName"
                              id="LastName"
                              className="form-control"
                              value={values.LastName}
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              invalid={Boolean(
                                touched["LastName"] && errors["LastName"]
                              )}
                            />
                            {touched["LastName"] && errors["LastName"] ? (
                              <FormFeedback>{errors["LastName"]}</FormFeedback>
                            ) : (
                              ""
                            )}
                          </Col>
                        </Row>
                        <Row className="mb-4">
                          <Col xs="12">
                            <label htmlFor="EmailAddress">
                              Email Address:
                            </label>
                            <Input
                              name="EmailAddress"
                              id="EmailAddress"
                              className="form-control"
                              value={values.EmailAddress}
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              invalid={Boolean(
                                touched["EmailAddress"] && errors["EmailAddress"]
                              )}
                            />
                            {touched["EmailAddress"] && errors["EmailAddress"] ? (
                              <FormFeedback>{errors["EmailAddress"]}</FormFeedback>
                            ) : (
                              ""
                            )}
                          </Col>
                        </Row>
                        <Row className="mb-4">
                          <Col xs="12">
                            <label className="required" htmlFor="Address1">
                              Address 1:
                            </label>
                            <Input
                              name="Address1"
                              id="Address1"
                              value={values.Address1}
                              className="form-control"
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              invalid={Boolean(
                                touched["Address1"] && errors["Address1"]
                              )}
                            />
                            {touched["Address1"] && errors["Address1"] ? (
                              <FormFeedback>{errors["Address1"]}</FormFeedback>
                            ) : (
                              ""
                            )}
                          </Col>
                        </Row>
                        <Row className="mb-4">
                          <Col xs="12">
                            <label htmlFor="Address2">Address 2:</label>
                            <Input
                              name="Address2"
                              id="Address2"
                              className="form-control"
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                            />
                          </Col>
                        </Row>
                        <Row className="mb-4">
                          <Col xs="12">
                            <label className="required" htmlFor="Country">
                              Country:
                            </label>
                            <Input
                              type="select"
                              name="Country"
                              className="form-control"
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              invalid={Boolean(
                                touched["Country"] && errors["Country"]
                              )}
                            >
                              <SelectOption key="" label="Select..." value="" />
                              {countriesArray.map((option) => (
                                <SelectOption
                                  key={option}
                                  label={option}
                                  value={option}
                                  selected={option === values.Country}
                                />
                              ))}
                            </Input>
                            {touched["Country"] && errors["Country"] ? (
                              <FormFeedback>{errors["Country"]}</FormFeedback>
                            ) : (
                              ""
                            )}
                          </Col>
                        </Row>
                        {values.Country === "USA" && (
                          <Row className="mb-4">
                            <Col xs="12">
                              <label
                                className={
                                  values.Country === "USA" ? "required" : ""
                                }
                                htmlFor="State"
                              >
                                State:
                              </label>
                              <Input
                                type="select"
                                name="State"
                                value={values.State}
                                className="form-control"
                                onChange={(e) => {
                                  handleChange(e);
                                }}
                                onBlur={(e) => {
                                  handleBlur(e);
                                }}
                                invalid={Boolean(
                                  touched["State"] && errors["State"]
                                )}
                              >
                                <SelectOption key="" label="Select..." value="" />
                                {statesArray.map((option) => (
                                  <SelectOption
                                    key={option}
                                    label={option}
                                    value={option}
                                    selected={option === values.State}
                                  />
                                ))}
                              </Input>
                              {touched["State"] && errors["State"] ? (
                                <FormFeedback>{errors["State"]}</FormFeedback>
                              ) : (
                                ""
                              )}
                            </Col>
                          </Row>
                        )}

                        {values.Country === "Canada" && (
                          <Row className="mb-4">
                            <Col xs="12">
                              <label
                                className={
                                  values.Country === "Canada" ? "required" : ""
                                }
                                htmlFor="Province"
                              >
                                Province:
                              </label>
                              <Input
                                type="select"
                                name="Province"
                                value={values.Province}
                                className="form-control"
                                onChange={(e) => {
                                  handleChange(e);
                                }}
                                onBlur={(e) => {
                                  handleBlur(e);
                                }}
                                invalid={Boolean(
                                  touched["Province"] && errors["Province"]
                                )}
                              >
                                <SelectOption key="" label="Select..." value="" />
                                {provincesArray.map((option) => (
                                  <SelectOption
                                    key={option}
                                    label={option}
                                    value={option}
                                    selected={option === values.Province}
                                  />
                                ))}
                              </Input>
                              {touched["Province"] && errors["Province"] ? (
                                <FormFeedback>
                                  {errors["Province"]}
                                </FormFeedback>
                              ) : (
                                ""
                              )}
                            </Col>
                          </Row>
                        )}
                        <Row className="mb-4">
                          <Col xs="6">
                            <label className="required" htmlFor="City">
                              City:
                            </label>
                            <Input
                              name="City"
                              id="City"
                              value={values.City}
                              className="form-control"
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              invalid={Boolean(
                                touched["City"] && errors["City"]
                              )}
                            />
                            {touched["City"] && errors["City"] ? (
                              <FormFeedback>{errors["City"]}</FormFeedback>
                            ) : (
                              ""
                            )}
                          </Col>
                          <Col xs="6">
                            <label
                              className={
                                values.Country === "USA" ||
                                values.Country === "Canada"
                                  ? "required"
                                  : ""
                              }
                              htmlFor="ZipPostalCode"
                            >
                              Zip/Postal Code:
                            </label>
                            <Input
                              name="ZipPostalCode"
                              id="ZipPostalCode"
                              value={values.ZipPostalCode}
                              className="form-control"
                              onBlur={(e) => {
                                handleBlur(e);
                              }}
                              onChange={(e) => {
                                handleChange(e);
                              }}
                              invalid={Boolean(
                                touched["ZipPostalCode"] &&
                                  errors["ZipPostalCode"]
                              )}
                            />
                            {touched["ZipPostalCode"] &&
                            errors["ZipPostalCode"] ? (
                              <FormFeedback>
                                {errors["ZipPostalCode"]}
                              </FormFeedback>
                            ) : (
                              ""
                            )}
                          </Col>
                        </Row>
                      </Container>
                      <Button type="submit" size="sm" color="primary">
                        Save
                      </Button>
                    </Form>
                  )}
                </Formik>
              </Col>
            </Row>
          </Container>
        </ModalBody>
      </Modal>
    </Fragment>
  );
}

export default AddressBook;
